import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { NavLink, useLocation } from 'react-router-dom';
import { isActivePath, NAV_GROUPS } from '../router/navigation';
import { useNavItems } from '../router/useNavItems';
import { useNavBadges } from './useNavBadges';

export function NavList({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const { pathname } = useLocation();
  const items = useNavItems();
  const badges = useNavBadges(items);
  const groups = NAV_GROUPS.map((group) => ({ group, items: items.filter((i) => i.group === group) })).filter((g) => g.items.length > 0);

  return (
    <>
      {groups.map(({ group, items: groupItems }) => (
        <Box key={group} component="section" aria-label={group} sx={{ px: 1.25, pb: 2.25 }}>
          <Typography variant="caption" component="h2" sx={groupLabelSx}>{group}</Typography>
          <Stack component="ul" sx={{ listStyle: 'none', m: 0, p: 0, gap: 0.25 }}>
            {groupItems.map((item) => {
              const isActive = isActivePath(item.path, pathname);
              const Icon = item.icon;
              const count = item.badge ? badges[item.badge] : 0;
              return (
                <li key={item.path}>
                  <ButtonBase
                    component={NavLink}
                    to={item.path}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={onNavigate}
                    sx={navItemSx(isActive)}
                  >
                    <Icon size={16} strokeWidth={1.75} aria-hidden />
                    <Box component="span" sx={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}>{item.label}</Box>
                    {count > 0 ? <Box component="span" aria-label={`${count} waiting`} sx={badgeSx}>{count}</Box> : null}
                  </ButtonBase>
                </li>
              );
            })}
          </Stack>
        </Box>
      ))}
    </>
  );
}

const groupLabelSx = { display: 'block', px: 1.25, pb: 0.75, color: 'text.secondary', fontSize: '0.75rem', letterSpacing: '0.01em' } as const;

export const navItemSx = (isActive: boolean) => ({
  width: '100%',
  justifyContent: 'flex-start',
  gap: 1.5,
  minHeight: 36,
  px: 1.25,
  borderRadius: 1,
  fontSize: '0.84375rem',
  fontWeight: isActive ? 600 : 500,
  color: 'text.primary',
  bgcolor: isActive ? 'surface.sunken' : 'transparent',
  transition: 'background-color 120ms',
  '& svg': { color: isActive ? 'text.primary' : 'text.secondary', flexShrink: 0 },
  '&:hover': { bgcolor: isActive ? 'surface.sunken' : 'surface.subtle' },
});

const badgeSx = {
  minWidth: 18,
  height: 18,
  px: 0.75,
  borderRadius: 9,
  display: 'grid',
  placeItems: 'center',
  fontSize: '0.6875rem',
  fontWeight: 600,
  bgcolor: 'surface.accentSoft',
  color: 'primary.main',
} as const;
